import { motion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { ChevronRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { copy } from '../i18n/copy';
import { scrollToId } from '../utils/scroll';

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

export default function Hero() {
  const { t } = useLanguage();

  return (
    <section className="relative mx-auto flex max-w-5xl flex-col items-center px-5 pb-14 pt-14 text-center md:pt-20">
      {/* Reading robot, echoing the lab scene (decorative, large screens only) */}
      <motion.img
        src="/assets/robotlearn.png"
        alt=""
        aria-hidden="true"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 0.9, x: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="pointer-events-none absolute -start-40 top-8 hidden w-72 select-none [mask-image:radial-gradient(circle_at_center,black_55%,transparent_72%)] xl:block"
      />

      <motion.div variants={container} initial="hidden" animate="show" className="flex flex-col items-center">
        <motion.div
          variants={item}
          className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 text-xs font-semibold text-cyan-200"
        >
          <Sparkles className="size-3.5" />
          {t(copy.heroBadge)}
        </motion.div>

        <motion.h1
          variants={item}
          className="mt-6 text-6xl font-black tracking-tight text-white drop-shadow-[0_0_30px_rgba(56,189,248,0.35)] md:text-7xl"
        >
          Taa Sandbox
        </motion.h1>

        <motion.h2 variants={item} className="mt-4 text-2xl font-extrabold md:text-3xl">
          <span className="text-white">{t(copy.headline1)}</span>{' '}
          <span className="text-orange-400">{t(copy.headline2)}</span>{' '}
          <span className="text-violet-400">{t(copy.headline3)}</span>
        </motion.h2>

        <motion.p
          variants={item}
          className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300/90 md:text-lg"
        >
          {t(copy.subheadline)}
        </motion.p>

        <motion.div variants={item} className="mt-8">
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => scrollToId('missions')}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 px-8 py-3.5 text-base font-extrabold text-white shadow-[0_0_50px_-10px_rgba(56,189,248,0.8)]"
          >
            {t(copy.chooseMission)}
            <ChevronRight className="size-5 rtl:rotate-180" />
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
}
